import React, { FunctionComponent, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  QueryLoaderWrapper,
  Text
} from '@companion-professional/components';
import { CheckupTag } from '@companion-professional/dctypes';
import { QuickCheckupFormValues, useQuickCheckupCreate } from '../../hooks/useQuickCheckupCreate';
import { useCheckupUrl } from '../../hooks/useCheckupUrl';
import { useSupportsFuzzySearch } from '../../hooks/useSupportsFuzzySearch';
import { useAuthStore } from '../../state/auth';
import { USER_SELECTABLE_LOCATION_TAGS } from '../../lib/config';
import { CheckupLocationSelectionButtons } from '../CheckupDialogComponents';
import { LoadingMessage } from '../LoadingMessage';
import { FuzzyPetSearchForm } from './FuzzyPetSearchForm';

export type CheckupData = {
  clinicPetId: string;
  checkupId: string;
  checkupUrl: string;
};

interface CreateQuickCheckupProps {
  setCheckupData: (checkupData: CheckupData) => void;
  onPetNotFound: (values: { clinicPetId: string; locationTag: CheckupTag }) => void;
  autofillPetId?: string;
}

// CreateQuickCheckup is the first step of the quick checkup flow. The exact UI depends on whether the backend supports
// fuzzy patient/client search for the user's clinic:
//   * If supported, a search-driven autocomplete is shown (see FuzzyPetSearchForm).
//   * If not supported, the user enters a clinic pet id from their PIMs. If the pet is not found, the flow moves to
//     the AddPetName step.
export const CreateQuickCheckup: FunctionComponent<CreateQuickCheckupProps> = ({
  setCheckupData,
  onPetNotFound,
  autofillPetId
}) => {
  const { isPending: supportsCheckIsPending, error: supportsCheckError, supportsFuzzySearch } = useSupportsFuzzySearch();

  return (
    <QueryLoaderWrapper isPending={supportsCheckIsPending} error={supportsCheckError}>
      {supportsFuzzySearch ? (
        <FuzzyPetSearchForm setCheckupData={setCheckupData} />
      ) : (
        <ClinicPetIdForm
          setCheckupData={setCheckupData}
          onPetNotFound={onPetNotFound}
          autofillPetId={autofillPetId}
        />
      )}
    </QueryLoaderWrapper>
  );
};

// ClinicPetIdForm is the original Quick Checkup form: the user types a clinic pet id from their PIMs, and we create
// (or look up) a pet by that id.
const ClinicPetIdForm: FunctionComponent<CreateQuickCheckupProps> = ({
  setCheckupData,
  onPetNotFound,
  autofillPetId
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { register, handleSubmit, reset, formState, setValue } = useForm<QuickCheckupFormValues>();
  const { createQuickCheckup, error: createCheckupError, isPending: createCheckupIsPending } = useQuickCheckupCreate();
  const { getCheckupUrl, error: getCheckupUrlError, isPending: getCheckupUrlIsPending } = useCheckupUrl();

  return (
    <form
      onSubmit={handleSubmit((values) => {
        const clinicId = useAuthStore.getState().parsedToken?.clinic_id;
        if (!clinicId) {
          throw new Error('No clinic defined in the user token');
        }
        values.clinicId = clinicId;
        createQuickCheckup(values).then((result) => {
          if (result.type === 'pet_not_found') {
            onPetNotFound({ clinicPetId: values.clinicPetId, locationTag: values.locationTag });
            reset();
          } else {
            const checkupUrl = getCheckupUrl(result.response.id);
            if (checkupUrl) {
              setCheckupData({
                clinicPetId: values.clinicPetId,
                checkupId: result.response.id,
                checkupUrl
              });
            }
            reset();
          }
        });
      })}
      ref={formRef}
    >
      <DialogHeader>
        <DialogTitle>Create Quick Checkup</DialogTitle>
        <DialogDescription>
          Enter the pet id from your Patient Information Management System. If the pet id doesn't exist in our system,
          we will create a new pet entry.
        </DialogDescription>
      </DialogHeader>
      <QueryLoaderWrapper
        isPending={getCheckupUrlIsPending || createCheckupIsPending}
        error={getCheckupUrlError || createCheckupError}
        showChildrenOnError
      >
        <div className="mt-4 flex flex-col space-y-8">
          <div className="flex flex-col space-y-2">
            <Label htmlFor="clinicPetId">Pet Id (from PIMs)</Label>
            <Input
              id="clinicPetId"
              aria-invalid={formState.errors.clinicPetId ? 'true' : 'false'}
              defaultValue={autofillPetId}
              {...register('clinicPetId', { required: true })}
            />
            {formState.errors.clinicPetId && formState.errors.clinicPetId.type === 'required' ? (
              // TODO: This should be it's own <InputError /> component
              <Text as="span" tone="destructive">
                The Pet Id is required.
              </Text>
            ) : null}
          </div>
          <div className="flex flex-col space-y-2">
            <Label description="Select the location that the video will be taken.">Create Checkup For:</Label>
            {getCheckupUrlIsPending || createCheckupIsPending ? (
              <LoadingMessage className="ml-3">Creating Checkup...</LoadingMessage>
            ) : (
              <CheckupLocationSelectionButtons
                onTagSelect={async (tag) => {
                  setValue('locationTag', tag);
                }}
                locationTags={USER_SELECTABLE_LOCATION_TAGS}
                buttonType="submit"
              />
            )}
          </div>
        </div>
      </QueryLoaderWrapper>
    </form>
  );
};
